import * as OAuth from '@mail/oauth';
import { prepareCategoryByURL } from '@mail-core/dashboard/keeper/metrics.api';
import { captureMessage } from '@sentry/browser';
import axios, { AxiosResponse } from 'axios';
import config from 'Cloud/config';
import qs from 'qs';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { sendKaktamLog, sendXray } from 'reactApp/utils/ga';

const CLIENT_ID = config.get('CORS_API_CLIENT_ID');
const OAUTH_HOST = config.get('OAUTH_HOST');
const CORSAPI_HOST = config.get('CORS_API_HOST');
const login = config.get('user.email');
const ATTACHES_LOAD_LIMIT = getLoadMoreLimit(EStorageType.attaches);
let OAuthToken: string | null = '';

const axiosInstance = axios.create({
    baseURL: `https://${CORSAPI_HOST}/api/v1/`,
    method: 'POST',
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: false,
});

export function checkAuth() {
    const clientId = CLIENT_ID;
    const timeout = setTimeout(function () {
        sendXray('oauth', 'timeout');
    }, 5000);

    return new Promise(function (resolve, reject) {
        // инициализация OAuth клиента
        OAuth.init({
            clientId,
            login,
        });
        OAuth.setOAuthHost(OAUTH_HOST);

        // запрос за авторизацией, работает только с доменов, привязанных к clientId
        OAuth.auth(function (state) {
            clearTimeout(timeout);
            sendXray('oauth', state.status);

            const isAuth = state.status === 'connected';
            OAuthToken = state.access_token;

            if (isAuth) {
                resolve(OAuthToken);
            } else {
                sendKaktamLog(
                    {
                        state,
                        login,
                        clientId,
                    },
                    'cloud_oauth'
                );

                reject(`oauth: ${state.status}`);
            }
        });
    });
}

function api(method, data?): Promise<AxiosResponse | any> {
    return new Promise(function (resolve, reject) {
        const category = prepareCategoryByURL(method);
        axiosInstance(method, {
            data: (function (data) {
                data.access_token = OAuthToken;

                return Object.keys(data)
                    .filter((key) => data[key] !== undefined && data[key] !== '')
                    .map(function (p) {
                        return `${encodeURIComponent(p)}=${encodeURIComponent(data[p])}`;
                    })
                    .join('&');
            })(data || {}),
        })
            .then(function (response) {
                if (response.status === 200) {
                    resolve(response.data);
                } else {
                    reject(response);
                    sendKaktamLog(response, 'cloud_corsapi');
                }

                sendXray('corsapi', `${category}_${response.status}`);
            })
            .catch(function (error) {
                sendKaktamLog(error, 'cloud_corsapi');
                sendXray('corsapi', `${category}_error`);

                reject(error);
            });
    });
}

export function getFolders() {
    return checkAuth()
        .then(function () {
            return api('folders');
        })
        .then(function (response) {
            return response.body;
        });
}

export function getAttaches({
    folderId = -1,
    after = '',
    before = '',
    limit = ATTACHES_LOAD_LIMIT,
    extraThumbs = { image: '167x167' },
    query = '',
    type = '',
} = {}) {
    return checkAuth()
        .then(() =>
            api('messages/attaches/search/clear', {
                folder_id: folderId,
                extra_thumbs: JSON.stringify(extraThumbs),
                after,
                before,
                limit,
                query,
                type,
            })
        )
        .then(function (response) {
            return response.body;
        });
}

export function getMessageAttaches({ messageId, email }: { messageId?: string; email?: string } = {}) {
    return checkAuth()
        .then(function () {
            return api('messages/attaches', {
                id: messageId,
                email,
            });
        })
        .then(function (response) {
            if (response.status === 200) {
                return response.body || response.map((a) => a.body);
            }
            sendKaktamLog(
                { messageId, text: 'getMessageAttaches api error', status: response.status, body: response.body },
                'attaches_api'
            );
            captureMessage(`getMessageAtt error: status ${response.status} `, { extra: response.body });
            sendXray(['getmessageatt', 'status', response.status]);
            return null; // TODO: show message?
        });
}

export function getTemporaryMessageAttaches({ messageId, email }: { messageId?: string; email?: string } = {}) {
    return checkAuth()
        .then(function () {
            return api('messages/attaches/temporary', {
                message_id: messageId,
                email,
            });
        })
        .then(function (response) {
            if (response.status === 200) {
                return response.body || response.map((a) => a.body);
            }
            sendKaktamLog(
                { messageId, text: 'getTemporaryMessageAttaches api error', status: response.status, body: response.body },
                'attaches_api'
            );
            captureMessage(`getTempMessage error: status ${response.status} `, { extra: response.body });
            sendXray(['gettempmessage', 'status', response.status]);
            return null;
        });
}

export function getMessage({ messageId, email, folderId }: { messageId?: string; email?: string; folderId?: string } = {}) {
    return checkAuth()
        .then(function () {
            return api('messages/message', {
                id: messageId,
                email,
                folder_id: folderId || undefined,
            });
        })
        .then(function (response) {
            if (response.status === 200) {
                return response.body || response.map((a) => a.body);
            }
            sendKaktamLog({ messageId, text: 'getMessage api error', status: response.status, body: response.body }, 'attaches_api');
            captureMessage(`getMessage error: status ${response.status} `, { extra: response.body });
            sendXray(['getmessage', 'status', response.status]);
            return null;
        });
}

export function getDocumentUrl({ url }: { url?: string } = {}) {
    return checkAuth()
        .then(function () {
            return api(url);
        })
        .then(function (response) {
            return response.body.url;
        });
}

export function cloneAttach({ ids, folder, email }: { ids?: any; folder?: any; email?: string } = {}) {
    return checkAuth()
        .then(function () {
            return api('attaches/add/to-cloud', {
                folder,
                email,
                ids: JSON.stringify(ids),
                htmlencoded: false,
            });
        })
        .then(function (response) {
            const res = response.body || response.map((a) => a.body);
            if (response.status === 200) {
                return res;
            }
            sendKaktamLog({ ids, folder, text: 'cloneAttach api error', status: response.status, body: response.body }, 'attaches_api');
            return Promise.reject({ body: res, status: response.status });
        });
}

export const downloadDocumentApi = (url: string): Promise<{ data: string | null }> =>
    checkAuth().then(() =>
        axios
            .post(url, qs.stringify({ access_token: OAuthToken }), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                responseType: 'arraybuffer',
            })
            .catch((error) => {
                sendXray('down_doc_err');
                sendKaktamLog(error, 'down_doc_err');

                sendKaktamLog({ url, text: 'downloadDocumentApi api error', error }, 'down_doc_err');
                return { data: null };
            })
    );

export const removeMessages = (email: string, ids: string[]) =>
    checkAuth().then(() =>
        api('messages/remove', {
            email,
            ids: JSON.stringify(ids),
        })
    );
